/**
 * Who is signed in. /auth/me is the source of truth; her account's language
 * wins over the device's once she is signed in (doc 07 §1).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, type ReactNode } from 'react';

import { ApiError, api } from './core/api';
import { forgetSavedData } from './core/cache';
import { forgetPushToken, forgetThisDevice, initPushNotifications, syncNotificationBadge } from './core/push';
import { setLocale } from './i18n';
import type { CurrentUser } from './types';

interface Auth {
  user: CurrentUser | null;
  loading: boolean;
  signedIn: (user: CurrentUser) => void;
  signOut: () => Promise<void>;
  /** After DELETE /auth/me: the same wipe as signOut, without the calls the
   *  server would now refuse (D-89). */
  accountDeleted: () => Promise<void>;
}

const AuthContext = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.getMe();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
  const user = me.data ?? null;

  useEffect(() => {
    if (user) void setLocale(user.locale);
  }, [user]);

  // The server says nobody is signed in (401), e.g. a session that expired
  // while a saved copy was on the device: nothing of that account may stay (D-87).
  const signedOut = me.isSuccess && me.data === null;
  useEffect(() => {
    if (!signedOut) return;
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    forgetSavedData();
  }, [signedOut, queryClient]);

  // Right after sign-in, the iPhone asks once for notifications (doc 05 §6).
  const signedInId = user?.id;
  useEffect(() => {
    if (signedInId) void initPushNotifications();
  }, [signedInId]);

  const forgetLocally = () => {
    // Null the user in place first: clear() would drop the query the
    // provider is subscribed to, and a later setQueryData never reaches it,
    // so the screen stayed signed in until a reload.
    queryClient.setQueryData(['me'], null);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    forgetSavedData();
  };

  const value: Auth = {
    user,
    loading: me.isPending,
    signedIn: (u) => queryClient.setQueryData(['me'], u),
    signOut: async () => {
      await forgetThisDevice();
      await api.logout();
      forgetLocally();
    },
    accountDeleted: async () => {
      // The server already dropped this device's push token with the account.
      forgetPushToken();
      await syncNotificationBadge(0);
      forgetLocally();
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
